import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { FormDialog, FormTextField, LoadingIndicator } from '../../../design-system';
import { useUnsavedChangesGuard } from '../../../shared/useUnsavedChangesGuard';
import type { StaffingOperationsPort } from '../operations/port';
import type { StaffingLifecycleChange, StaffingLifecycleDraft, StaffingReference, StaffingWorker } from '../operations/model';
import { personnelActionTitles, type WorkerPersonnelAction } from '../operations/personnelActions';
import { fixtureStaffingWorkers } from '../operations/fixtures';
import { WorkerPersonnelActionDialog } from './WorkerPersonnelActionDialog';

export type WorkerPersonnelRequestTarget = {
  id: string;
  processType: string;
  status: string;
  correctionOf?: unknown;
  transitionKind?: unknown;
  worker?: StaffingReference;
  effectiveDate?: string;
};
export function isManagedPersonnelRequest(target: WorkerPersonnelRequestTarget | undefined) {
  return Boolean(target && !target.correctionOf && !target.transitionKind && ['LEAVE_OF_ABSENCE', 'RETURN_FROM_LEAVE', 'END_WORKER'].includes(target.processType));
}

type EditorSession = { open: boolean; worker: StaffingWorker; draft: StaffingLifecycleDraft; mode: 'edit' | 'copy' };
export type WorkerPersonnelRequestActionsProps = {
  target: WorkerPersonnelRequestTarget;
  port: StaffingOperationsPort;
  fixtureMode: boolean;
  canEdit: boolean;
  canCancel: boolean;
  disabled?: boolean;
  onChanged: () => void;
  onBusyChange?: (busy: boolean) => void;
  onOpenWorkflow?: (reference: StaffingReference) => void;
};

// 업무 초안의 변경은 staffing 포트만 사용하고 원 요청의 댓글·승인 이력은 그대로 보존합니다.
export function WorkerPersonnelRequestActions({ target, port, fixtureMode, canEdit, canCancel, disabled = false, onChanged, onBusyChange, onOpenWorkflow }: WorkerPersonnelRequestActionsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<EditorSession>();
  const [cancellation, setCancellation] = useState<StaffingLifecycleChange>();
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelUncertain, setCancelUncertain] = useState(false);
  const cancelIntent = useRef<{ key: string; comment: string; processId: string } | undefined>(undefined);
  const active = useRef(false);
  const editorChanged = useRef(false);
  const currentTarget = useRef(target.id);
  currentTarget.current = target.id;
  const supported = isManagedPersonnelRequest(target);
  const editable = supported && canEdit && Boolean(fixtureMode || port.getWorkerLifecycleDraft) && ['DRAFT', 'DENIED'].includes(target.status);
  const cancellable = supported && canCancel && Boolean(fixtureMode || (port.getWorkerLifecycle && port.cancelWorkerLifecycle)) && ['DRAFT', 'IN_PROGRESS'].includes(target.status);
  const busy = loading || cancelBusy;
  useEffect(() => { onBusyChange?.(busy || cancelUncertain); return () => onBusyChange?.(false); }, [busy, cancelUncertain, onBusyChange]);
  useUnsavedChangesGuard({ dirty: Boolean(cancellation && cancelReason.trim() && !cancelUncertain), busy: cancelBusy || cancelUncertain });

  const fixtureChange = (): StaffingLifecycleChange => ({ id: target.id, processType: target.processType as StaffingLifecycleChange['processType'], status: target.status, effectiveDate: target.effectiveDate ?? new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()), worker: target.worker, businessProcess: { id: `fixture-process-${target.id}` }, version: 1 });
  const fetchChange = () => fixtureMode ? Promise.resolve(fixtureChange()) : port.getWorkerLifecycle ? port.getWorkerLifecycle(target.id) : Promise.reject(new Error('발령 상태를 확인할 수 없습니다.'));
  const openEditor = async () => {
    if (!editable || disabled || active.current) return;
    if (editor) { setEditor({ ...editor, open: true }); return; }
    const id = target.id;
    let stale = false;
    active.current = true; setLoading(true); setError('');
    try {
      const detail = fixtureMode ? { change: fixtureChange(), version: 1, input: { processType: target.processType as StaffingLifecycleDraft['input']['processType'], effectiveDate: fixtureChange().effectiveDate, reason: '' } }
        : port.getWorkerLifecycleDraft ? await port.getWorkerLifecycleDraft(id) : undefined;
      if (!detail || detail.change.id !== id || !isManagedPersonnelRequest(detail.change) || detail.change.status !== target.status || !['DRAFT', 'DENIED'].includes(detail.change.status)) { stale = true; throw new Error('요청 상태가 변경되었습니다. 최신 내용을 확인해 주세요.'); }
      if (!detail.change.worker?.id) throw new Error('대상 구성원을 확인할 수 없습니다.');
      const worker = fixtureMode ? fixtureStaffingWorkers.find(item => item.id === detail.change.worker!.id) : await port.getWorker(detail.change.worker.id, detail.input.effectiveDate);
      if (!worker) throw new Error('대상 구성원을 확인할 수 없습니다.');
      if (currentTarget.current !== id) return;
      setEditor({ open: true, worker, draft: detail, mode: detail.change.status === 'DENIED' ? 'copy' : 'edit' });
    } catch (failure) { if (currentTarget.current === id) { setError(failure instanceof Error ? failure.message : '초안을 열지 못했습니다.'); if (stale || (failure && typeof failure === 'object' && 'code' in failure && failure.code === 'INVALID_STATE')) onChanged(); } }
    finally { active.current = false; setLoading(false); }
  };
  const openCancel = async () => {
    if (!cancellable || disabled || active.current) return;
    const id = target.id;
    let stale = false;
    active.current = true; setLoading(true); setError('');
    try {
      const change = await fetchChange();
      if (change.id !== id || !isManagedPersonnelRequest(change) || !['DRAFT', 'IN_PROGRESS'].includes(change.status) || !change.businessProcess?.id) { stale = true; throw new Error('현재 요청 상태에서는 취소할 수 없습니다. 최신 내용을 확인해 주세요.'); }
      if (currentTarget.current !== id) return;
      setCancellation(change); setCancelReason(''); cancelIntent.current = undefined; setCancelUncertain(false);
    } catch (failure) { if (currentTarget.current === id) { setError(failure instanceof Error ? failure.message : '요청 상태를 확인하지 못했습니다.'); if (stale) onChanged(); } }
    finally { active.current = false; setLoading(false); }
  };
  const finishCancel = () => { setCancellation(undefined); cancelIntent.current = undefined; setCancelUncertain(false); setError(''); onChanged(); };
  const cancel = async () => {
    if (!cancellation || !canCancel || disabled || active.current || (!cancelReason.trim() && cancellation.status !== 'DRAFT')) return;
    const id = target.id;
    if (!cancelIntent.current) cancelIntent.current = { key: crypto.randomUUID(), comment: cancelReason.trim(), processId: cancellation.businessProcess.id };
    const intent = cancelIntent.current;
    active.current = true; setCancelBusy(true); setError('');
    try {
      if (!fixtureMode) {
        if (!port.cancelWorkerLifecycle) throw new Error('요청 취소 기능을 사용할 수 없습니다.');
        const result = await port.cancelWorkerLifecycle(intent.processId, intent.comment, intent.key);
        if (result.status !== 'CANCELLED') throw new Error('취소 결과를 확인하지 못했습니다.');
      }
      if (currentTarget.current === id) finishCancel();
    } catch (failure) {
      if (currentTarget.current !== id) return;
      const status = failure && typeof failure === 'object' && 'status' in failure ? Number(failure.status) : undefined;
      const uncertain = status === undefined || status >= 500 || status === 408 || status === 429;
      setCancelUncertain(uncertain);
      if (!uncertain) cancelIntent.current = undefined;
      try {
        const latest = await fetchChange();
        if (currentTarget.current !== id) return;
        if (latest.status === 'CANCELLED') { finishCancel(); return; }
        if (!['DRAFT', 'IN_PROGRESS'].includes(latest.status)) { setCancelUncertain(false); cancelIntent.current = undefined; setCancellation(undefined); onChanged(); }
      } catch { /* 결과 확인에 실패해도 같은 취소 의도로 재시도할 수 있도록 키를 보존합니다. */ }
      setError(failure instanceof Error ? failure.message : '취소 결과를 확인하지 못했습니다.');
    } finally { active.current = false; setCancelBusy(false); }
  };
  if (!editable && !cancellable && !editor && !cancellation) return null;
  return <>
    <Stack spacing={1} sx={{ width: '100%' }}>
      {error && !cancellation && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {cancellable && <Button variant="outlined" color="error" disabled={disabled || busy || cancelUncertain} onClick={() => void openCancel()}>{target.status === 'DRAFT' ? '초안 폐기' : '요청 취소'}</Button>}
        {editable && <Button variant="contained" disabled={disabled || busy || cancelUncertain} onClick={() => void openEditor()}>{target.status === 'DENIED' ? '복사해서 새 요청' : '이어서 작성'}</Button>}
      </Stack>
      {loading && <LoadingIndicator label="인사 변경 요청 확인" />}
    </Stack>
    {editor && <WorkerPersonnelActionDialog key={`${editor.draft.change.id}-${editor.mode}`} open={editor.open} action={editor.draft.input.processType as WorkerPersonnelAction} worker={editor.worker} asOf={editor.draft.input.effectiveDate} port={port} fixtureMode={fixtureMode} source={{ mode: editor.mode, draft: editor.draft }} onClose={retain => { setEditor(retain ? { ...editor, open: false } : undefined); if (!retain && editorChanged.current) { editorChanged.current = false; onChanged(); } }} onChanged={() => { editorChanged.current = true; }} onOpenWorkflow={onOpenWorkflow} />}
    <FormDialog open={Boolean(cancellation)} title={cancellation?.status === 'DRAFT' ? '초안을 폐기할까요?' : '요청을 취소할까요?'} busy={cancelBusy} onClose={() => { if (!cancelUncertain && !cancelBusy) { setCancellation(undefined); setError(''); } }} onSubmit={cancel} submitLabel={cancelUncertain ? '다시 시도' : cancellation?.status === 'DRAFT' ? '초안 폐기' : '요청 취소'} submitColor="error" cancelLabel="돌아가기" submitDisabled={!canCancel || (cancellation?.status !== 'DRAFT' && !cancelReason.trim())}>
      <Stack spacing={2}>
        <Typography>{cancellation?.worker?.descriptor ?? cancellation?.person?.descriptor ?? '대상 구성원'} · {personnelActionTitles[target.processType as WorkerPersonnelAction]}</Typography>
        <Typography variant="body2" color="text.secondary">{cancellation?.status === 'DRAFT' ? '초안을 폐기하면 다시 제출할 수 없습니다.' : '진행 중인 결재를 중단합니다. 취소 사유는 이력에 남습니다.'}</Typography>
        {cancellation?.status !== 'DRAFT' && <FormTextField label="취소 사유" required value={cancelReason} onChange={event => setCancelReason(event.target.value)} disabled={cancelBusy || cancelUncertain} multiline minRows={2} slotProps={{ htmlInput: { maxLength: 2000 } }} />}
        {cancelUncertain && <Alert severity="warning">취소 결과를 확인하지 못했습니다. 같은 요청으로 다시 시도해 주세요.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </FormDialog>
  </>;
}
